import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Spin,
  Tabs,
  notification,
  Alert,
  Modal,
  Select,
  Input,
  Tag,
  Timeline,
  Table,
  Tooltip,
  Dropdown,
  Progress,
  Badge,
  Empty,
  Popconfirm,
  Avatar,
  Divider,
} from "antd";
import {
  FiUser,
  FiSettings,
  FiFileText,
  FiDollarSign,
  FiClock,
  FiArrowLeft,
  FiArrowRight,
  FiZap,
  FiCreditCard,
  FiRefreshCw,
  FiUsers,
  FiShield,
  FiActivity,
  FiMessageSquare,
  FiMail,
  FiMoreVertical,
  FiEdit3,
  FiTrash2,
  FiLock,
  FiUnlock,
  FiLogIn,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiGlobe,
  FiMonitor,
  FiSmartphone,
  FiMapPin,
  FiCalendar,
  FiTrendingUp,
  FiTrendingDown,
  FiPlus,
  FiSend,
  FiCopy,
  FiExternalLink,
  FiDownload,
  FiEye,
  FiUserPlus,
  FiUserMinus,
  FiStar,
  FiHeart,
  FiAward,
} from "react-icons/fi";
import {
  RiVipCrownLine,
  RiShieldCheckLine,
  RiSpyLine,
  RiHistoryLine,
} from "react-icons/ri";
import {
  MetaData,
  Analytics,
  UsageDetails,
  CurrentSubscription,
} from "@components";
import {
  getAdminDetailsById,
  toggleDisableUserAccount,
  manualRefillAdmin,
  changeAdminPlan,
} from "@api/user/superAdmin";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router";
import "@assets/css/AdminDetails.css";

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

// Animated Number Component
const AnimatedNumber = ({ value, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startTimeRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const targetValue = typeof value === "number" ? value : 0;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const progress = timestamp - startTimeRef.current;
      const percentage = Math.min(progress / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
      setDisplayValue(Math.floor(easeOutQuart * targetValue));

      if (percentage < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    startTimeRef.current = null;
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration]);

  return <>{displayValue.toLocaleString()}</>;
};

// Account Health Score Component
const AccountHealthScore = ({ adminDetails }) => {
  const calculateHealthScore = () => {
    let score = 0;
    const factors = [];

    // Email verified (20 points)
    if (adminDetails?.adminMetaData?.emailVerified) {
      score += 20;
      factors.push({ label: "Email verified", positive: true });
    } else {
      factors.push({ label: "Email not verified", positive: false });
    }

    // Active subscription (25 points)
    if (adminDetails?.currentSubscription?.status === "active") {
      score += 25;
      factors.push({ label: "Active subscription", positive: true });
    } else {
      factors.push({ label: "No active subscription", positive: false });
    }

    // Recent activity - logged in within 7 days (20 points)
    const lastActive = adminDetails?.adminMetaData?.lastActive;
    if (lastActive) {
      const daysSinceActive = Math.floor(
        (Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceActive <= 7) {
        score += 20;
        factors.push({ label: "Active in last 7 days", positive: true });
      } else if (daysSinceActive <= 30) {
        score += 10;
        factors.push({ label: "Active in last 30 days", positive: true });
      } else {
        factors.push({ label: "Inactive for 30+ days", positive: false });
      }
    }

    // Has generations (20 points)
    if (adminDetails?.analytics?.totalGenerations > 0) {
      score += 20;
      factors.push({ label: "Has used the product", positive: true });
    } else {
      factors.push({ label: "No product usage", positive: false });
    }

    // Account not disabled (15 points)
    if (!adminDetails?.adminMetaData?.disabled) {
      score += 15;
      factors.push({ label: "Account active", positive: true });
    } else {
      factors.push({ label: "Account disabled", positive: false });
    }

    return { score, factors };
  };

  const { score, factors } = calculateHealthScore();
  const getScoreColor = () => {
    if (score >= 80) return "#10b981";
    if (score >= 60) return "#f59e0b";
    if (score >= 40) return "#f97316";
    return "#ef4444";
  };

  return (
    <div className="admin-health-score">
      <div className="health-score-header">
        <span className="health-score-label">Account Health</span>
        <Tooltip
          title={
            <div>
              {factors.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  {f.positive ? (
                    <FiCheckCircle color="#10b981" size={12} />
                  ) : (
                    <FiXCircle color="#ef4444" size={12} />
                  )}
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          }
        >
          <Progress
            type="circle"
            percent={score}
            width={60}
            strokeColor={getScoreColor()}
            format={(percent) => (
              <span style={{ color: getScoreColor(), fontWeight: 700 }}>{percent}</span>
            )}
          />
        </Tooltip>
      </div>
    </div>
  );
};

// Team Member Card Component
const TeamMemberCard = ({ member, onRemove, onToggleRole }) => (
  <div className="admin-team-member-card">
    <div className="team-member-avatar">
      {member.avatar ? (
        <Avatar src={member.avatar} size={40} />
      ) : (
        <Avatar size={40} style={{ background: "#8B5CF6" }}>
          {(member.name || member.email || "U").charAt(0).toUpperCase()}
        </Avatar>
      )}
    </div>
    <div className="team-member-info">
      <div className="team-member-name">{member.name || "No Name"}</div>
      <div className="team-member-email">{member.email}</div>
    </div>
    <div className="team-member-role">
      <Tag color={member.role === "admin" ? "purple" : "default"}>
        {member.role || "member"}
      </Tag>
    </div>
    <div className="team-member-actions">
      <Dropdown
        trigger={["click"]}
        overlay={
          <div className="admin-dropdown-menu">
            <button className="admin-dropdown-item" onClick={() => onToggleRole(member)}>
              <RiVipCrownLine size={14} />
              {member.role === "admin" ? "Remove Admin" : "Make Admin"}
            </button>
            <div className="admin-dropdown-divider" />
            <button className="admin-dropdown-item danger" onClick={() => onRemove(member)}>
              <FiUserMinus size={14} />
              Remove from Team
            </button>
          </div>
        }
      >
        <Button type="text" icon={<FiMoreVertical />} />
      </Dropdown>
    </div>
  </div>
);

// Activity Item Component
const ActivityItem = ({ activity }) => {
  const getActivityIcon = () => {
    switch (activity.type) {
      case "login": return <FiLogIn className="activity-icon login" />;
      case "logout": return <FiLogIn className="activity-icon logout" style={{ transform: "rotate(180deg)" }} />;
      case "generation": return <FiZap className="activity-icon generation" />;
      case "subscription": return <FiCreditCard className="activity-icon subscription" />;
      case "settings": return <FiSettings className="activity-icon settings" />;
      case "team": return <FiUsers className="activity-icon team" />;
      case "security": return <FiShield className="activity-icon security" />;
      default: return <FiActivity className="activity-icon default" />;
    }
  };

  return (
    <div className="admin-activity-item">
      <div className="activity-icon-wrapper">{getActivityIcon()}</div>
      <div className="activity-content">
        <div className="activity-title">{activity.title}</div>
        <div className="activity-description">{activity.description}</div>
        {activity.metadata && (
          <div className="activity-metadata">
            {activity.metadata.ip && (
              <span><FiGlobe size={12} /> {activity.metadata.ip}</span>
            )}
            {activity.metadata.device && (
              <span><FiMonitor size={12} /> {activity.metadata.device}</span>
            )}
            {activity.metadata.location && (
              <span><FiMapPin size={12} /> {activity.metadata.location}</span>
            )}
          </div>
        )}
      </div>
      <div className="activity-time">
        {new Date(activity.timestamp).toLocaleString()}
      </div>
    </div>
  );
};

// Security Session Card Component
const SessionCard = ({ session, onRevoke, isCurrent }) => (
  <div className={`admin-session-card ${isCurrent ? "current" : ""}`}>
    <div className="session-device-icon">
      {session.deviceType === "mobile" ? (
        <FiSmartphone size={24} />
      ) : (
        <FiMonitor size={24} />
      )}
    </div>
    <div className="session-info">
      <div className="session-device">
        {session.browser} on {session.os}
        {isCurrent && <Tag color="green" style={{ marginLeft: 8 }}>Current</Tag>}
      </div>
      <div className="session-details">
        <span><FiMapPin size={12} /> {session.location || "Unknown location"}</span>
        <span><FiGlobe size={12} /> {session.ip}</span>
        <span><FiClock size={12} /> Last active: {new Date(session.lastActive).toLocaleString()}</span>
      </div>
    </div>
    {!isCurrent && (
      <Button
        type="text"
        danger
        icon={<FiXCircle />}
        onClick={() => onRevoke(session.id)}
      >
        Revoke
      </Button>
    )}
  </div>
);

// Note Card Component
const NoteCard = ({ note, onEdit, onDelete }) => (
  <div className="admin-note-card">
    <div className="note-header">
      <div className="note-author">
        <Avatar size={24} style={{ background: "#8B5CF6" }}>
          {(note.author?.name || "S").charAt(0)}
        </Avatar>
        <span>{note.author?.name || "Super Admin"}</span>
      </div>
      <div className="note-time">{new Date(note.createdAt).toLocaleString()}</div>
    </div>
    <div className="note-content">{note.content}</div>
    {note.tags && note.tags.length > 0 && (
      <div className="note-tags">
        {note.tags.map((tag, i) => (
          <Tag key={i} color="purple">{tag}</Tag>
        ))}
      </div>
    )}
    <div className="note-actions">
      <Button type="text" size="small" icon={<FiEdit3 size={14} />} onClick={() => onEdit(note)}>
        Edit
      </Button>
      <Popconfirm title="Delete this note?" onConfirm={() => onDelete(note._id)}>
        <Button type="text" size="small" danger icon={<FiTrash2 size={14} />}>
          Delete
        </Button>
      </Popconfirm>
    </div>
  </div>
);

const AdminDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const [adminDetails, setAdminDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [refillLoading, setRefillLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  // Modal states
  const [changePlanModalVisible, setChangePlanModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false);
  const [addNoteModalVisible, setAddNoteModalVisible] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [noteTags, setNoteTags] = useState([]);

  // Team data (mock - would come from API)
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  // Activity data (mock - would come from API)
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Security data (mock - would come from API)
  const [sessions, setSessions] = useState([]);
  const [securityLoading, setSecurityLoading] = useState(false);

  // Notes data (mock - would come from API)
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  const availablePlans = [
    { id: "free", name: "Free", color: "#64748b" },
    { id: "optimizer", name: "Optimizer", color: "#10b981" },
    { id: "wizard", name: "Wizard", color: "#8B5CF6" },
    { id: "maverick", name: "Maverick", color: "#ec4899" },
  ];

  useEffect(() => {
    fetchAdminDetails();
  }, []);

  const fetchAdminDetails = async () => {
    setLoading(true);
    try {
      const { data } = await getAdminDetailsById(id);
      setAdminDetails(data);

      // Mock team data - in production, this would come from the API
      setTeamMembers(data.teamMembers || []);

      // Mock activity data
      setActivities([
        {
          type: "login",
          title: "User logged in",
          description: "Successful login via email/password",
          timestamp: new Date(Date.now() - 1000 * 60 * 30),
          metadata: { ip: "192.168.1.1", device: "Chrome on Windows", location: "New York, US" },
        },
        {
          type: "generation",
          title: "Generated content",
          description: "Used AI to generate 5 responses",
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
        },
        {
          type: "settings",
          title: "Updated profile",
          description: "Changed notification preferences",
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
        },
      ]);

      // Mock session data
      setSessions([
        {
          id: "1",
          browser: "Chrome 120",
          os: "Windows 11",
          deviceType: "desktop",
          ip: "192.168.1.1",
          location: "New York, US",
          lastActive: new Date(),
          isCurrent: true,
        },
        {
          id: "2",
          browser: "Safari 17",
          os: "iOS 17",
          deviceType: "mobile",
          ip: "192.168.1.50",
          location: "New York, US",
          lastActive: new Date(Date.now() - 1000 * 60 * 60 * 24),
          isCurrent: false,
        },
      ]);

      // Mock notes data
      setNotes([
        {
          _id: "1",
          content: "Customer requested enterprise features demo. Follow up scheduled for next week.",
          author: { name: "Support Team" },
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
          tags: ["follow-up", "enterprise"],
        },
      ]);

    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to load admin details. Please try again.",
      });
      console.error("Error fetching admin details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserDisable = (userId) => {
    const isDisabled = adminDetails?.adminMetaData?.disabled;
    Modal.confirm({
      title: isDisabled ? "Enable Account" : "Disable Account",
      content: isDisabled
        ? "This will restore full access to this account."
        : "This will prevent the user from accessing their account.",
      okText: isDisabled ? "Enable" : "Disable",
      okType: isDisabled ? "primary" : "danger",
      onOk: () => {
        toggleDisableUserAccount(userId)
          .then((response) => {
            const status = response.data.disabled ? "disabled" : "enabled";
            notification.success({
              message: "Success",
              description: `Account has been ${status} successfully.`,
            });
            fetchAdminDetails();
          })
          .catch((err) => {
            notification.error({
              message: "Error",
              description: "Failed to update account status. Please try again.",
            });
            console.error("Toggle admin disable error", err);
          });
      },
    });
  };

  const handleManualRefill = async () => {
    setRefillLoading(true);
    try {
      await manualRefillAdmin(id);
      notification.success({
        message: "Success",
        description: "Credits have been refilled successfully.",
      });
      fetchAdminDetails();
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to refill credits. Please try again.",
      });
      console.error("Error refilling credits:", error);
    } finally {
      setRefillLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlan) return;

    try {
      await changeAdminPlan(id, selectedPlan);
      notification.success({
        message: "Success",
        description: "Plan has been changed successfully.",
      });
      setChangePlanModalVisible(false);
      setSelectedPlan(null);
      fetchAdminDetails();
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to change plan. Please try again.",
      });
      console.error("Error changing plan:", error);
    }
  };

  const handleImpersonate = () => {
    Modal.confirm({
      title: "Impersonate User",
      icon: <RiSpyLine size={24} color="#8B5CF6" />,
      content: (
        <div>
          <p>You are about to login as this user. This action will be logged.</p>
          <Alert
            type="warning"
            message="This feature should only be used for support purposes."
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: "Login as User",
      onOk: () => {
        // In production, this would generate an impersonation token
        notification.info({
          message: "Impersonation",
          description: "Impersonation feature will be available soon.",
        });
      },
    });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    // In production, this would call an API
    const note = {
      _id: Date.now().toString(),
      content: newNote,
      author: { name: "Super Admin" },
      createdAt: new Date(),
      tags: noteTags,
    };

    setNotes([note, ...notes]);
    setNewNote("");
    setNoteTags([]);
    setAddNoteModalVisible(false);

    notification.success({
      message: "Note Added",
      description: "Your note has been saved successfully.",
    });
  };

  const handleRevokeSession = (sessionId) => {
    Modal.confirm({
      title: "Revoke Session",
      content: "This will immediately log the user out of this device.",
      okText: "Revoke",
      okType: "danger",
      onOk: () => {
        setSessions(sessions.filter((s) => s.id !== sessionId));
        notification.success({
          message: "Session Revoked",
          description: "The session has been terminated.",
        });
      },
    });
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(adminDetails?.adminMetaData?.email || "");
    notification.success({ message: "Email copied to clipboard" });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  // Get stats from admin details
  const totalGenerations = adminDetails?.analytics?.totalGenerations || 0;
  const creditsAllotted = adminDetails?.currentUsageDetails?.creditsAllotted || 0;
  const creditsUsed = adminDetails?.currentUsageDetails?.creditsUsed || 0;
  const creditsRemaining = creditsAllotted - creditsUsed;
  const currentPlan = adminDetails?.currentUsageDetails?.subscriptionPlan || "Free";
  const subscriptionEndDate = adminDetails?.currentUsageDetails?.endDate || null;
  const lastActive = adminDetails?.adminMetaData?.lastActive || null;

  // Quick actions menu
  const quickActionsMenu = (
    <div className="admin-dropdown-menu" style={{ width: 220 }}>
      <button className="admin-dropdown-item" onClick={() => setSendEmailModalVisible(true)}>
        <FiMail size={15} />
        Send Email
      </button>
      <button className="admin-dropdown-item" onClick={handleCopyEmail}>
        <FiCopy size={15} />
        Copy Email
      </button>
      <div className="admin-dropdown-divider" />
      <button className="admin-dropdown-item" onClick={() => setChangePlanModalVisible(true)}>
        <RiVipCrownLine size={15} />
        Change Plan
      </button>
      <button className="admin-dropdown-item" onClick={handleManualRefill}>
        <FiRefreshCw size={15} />
        Refill Credits
      </button>
      <div className="admin-dropdown-divider" />
      <button className="admin-dropdown-item" onClick={handleImpersonate}>
        <RiSpyLine size={15} />
        Impersonate User
      </button>
      <button className="admin-dropdown-item" onClick={() => setAddNoteModalVisible(true)}>
        <FiMessageSquare size={15} />
        Add Note
      </button>
      <div className="admin-dropdown-divider" />
      <button
        className={`admin-dropdown-item ${adminDetails?.adminMetaData?.disabled ? "" : "danger"}`}
        onClick={() => handleToggleUserDisable(adminDetails?.adminMetaData?.user)}
      >
        {adminDetails?.adminMetaData?.disabled ? (
          <>
            <FiUnlock size={15} />
            Enable Account
          </>
        ) : (
          <>
            <FiLock size={15} />
            Disable Account
          </>
        )}
      </button>
    </div>
  );

  // Render stats cards with health score
  const renderStatsCards = () => {
    if (loading) {
      return (
        <div className="admin-details-loading">
          <Spin size="large" />
          <span className="admin-details-loading-text">Loading analytics...</span>
        </div>
      );
    }

    return (
      <Row gutter={[20, 20]} className="admin-details-stats-row">
        <Col xs={24} sm={12} lg={5}>
          <Card className="admin-details-stat-card" bordered={false}>
            <div className="admin-details-stat-icon purple">
              <FiZap />
            </div>
            <div className="admin-details-stat-label">Total Generations</div>
            <div className="admin-details-stat-value">
              <AnimatedNumber value={totalGenerations} />
            </div>
            <div className="admin-details-stat-subtitle">
              All-time AI generations
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <Card className="admin-details-stat-card" bordered={false}>
            <div className="admin-details-stat-icon green">
              <FiCreditCard />
            </div>
            <div className="admin-details-stat-label">Credits Remaining</div>
            <div className="admin-details-stat-value green">
              <AnimatedNumber value={creditsRemaining > 0 ? creditsRemaining : 0} />
            </div>
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={creditsAllotted > 0 ? Math.round((creditsUsed / creditsAllotted) * 100) : 0}
                size="small"
                strokeColor="#8B5CF6"
                showInfo={false}
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginTop: 4 }}>
                <span>{creditsUsed?.toLocaleString()} used</span>
                <span>{creditsAllotted?.toLocaleString()} total</span>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <Card className="admin-details-stat-card" bordered={false}>
            <div className="admin-details-stat-icon blue">
              <FiDollarSign />
            </div>
            <div className="admin-details-stat-label">Current Plan</div>
            <div className="admin-details-stat-value blue">{currentPlan}</div>
            <div className="admin-details-stat-subtitle">
              {subscriptionEndDate
                ? `Renews: ${new Date(subscriptionEndDate).toLocaleDateString()}`
                : "No active subscription"}
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <Card className="admin-details-stat-card" bordered={false}>
            <div className="admin-details-stat-icon orange">
              <FiClock />
            </div>
            <div className="admin-details-stat-label">Last Active</div>
            <div className="admin-details-stat-value orange" style={{ fontSize: "18px" }}>
              {lastActive
                ? new Date(lastActive).toLocaleDateString()
                : "N/A"}
            </div>
            <div className="admin-details-stat-subtitle">
              {lastActive
                ? new Date(lastActive).toLocaleTimeString()
                : "Never logged in"}
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={4}>
          <Card className="admin-details-stat-card" bordered={false}>
            <AccountHealthScore adminDetails={adminDetails} />
          </Card>
        </Col>
      </Row>
    );
  };

  // Render Team Management Tab
  const renderTeamTab = () => (
    <div className="admin-team-tab">
      <div className="admin-section-header">
        <div>
          <h3>Team Members</h3>
          <p>Manage team members under this admin account</p>
        </div>
        <Button
          className="admin-details-btn-primary"
          icon={<FiUserPlus style={{ marginRight: 6 }} />}
        >
          Invite Member
        </Button>
      </div>

      {teamMembers.length > 0 ? (
        <div className="admin-team-list">
          {teamMembers.map((member, index) => (
            <TeamMemberCard
              key={member._id || index}
              member={member}
              onRemove={(m) => {
                notification.info({ message: "Remove member: " + m.email });
              }}
              onToggleRole={(m) => {
                notification.info({ message: "Toggle role for: " + m.email });
              }}
            />
          ))}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No team members found"
        />
      )}

      <Alert
        className="admin-info-alert"
        type="info"
        showIcon
        message="Team Management"
        description="Team members share the admin's subscription and credits. The billing admin can invite up to the plan's team limit."
        style={{ marginTop: 24 }}
      />
    </div>
  );

  // Render Activity Log Tab
  const renderActivityTab = () => (
    <div className="admin-activity-tab">
      <div className="admin-section-header">
        <div>
          <h3>Activity Log</h3>
          <p>Complete audit trail of user actions</p>
        </div>
        <Button
          className="admin-details-btn-secondary"
          icon={<FiDownload style={{ marginRight: 6 }} />}
        >
          Export Log
        </Button>
      </div>

      {activities.length > 0 ? (
        <div className="admin-activity-list">
          {activities.map((activity, index) => (
            <ActivityItem key={index} activity={activity} />
          ))}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No activity recorded yet"
        />
      )}

      <div style={{ textAlign: "center", marginTop: 24 }}>
        <Button className="admin-details-btn-secondary">
          Load More
        </Button>
      </div>
    </div>
  );

  // Render Security Tab
  const renderSecurityTab = () => (
    <div className="admin-security-tab">
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <div className="admin-section-header">
            <div>
              <h3>Active Sessions</h3>
              <p>Devices currently logged into this account</p>
            </div>
            <Button
              type="text"
              danger
              icon={<FiXCircle style={{ marginRight: 6 }} />}
              onClick={() => {
                Modal.confirm({
                  title: "Revoke All Sessions",
                  content: "This will log the user out of all devices except current.",
                  okText: "Revoke All",
                  okType: "danger",
                  onOk: () => {
                    setSessions(sessions.filter((s) => s.isCurrent));
                    notification.success({ message: "All other sessions revoked" });
                  },
                });
              }}
            >
              Revoke All
            </Button>
          </div>

          <div className="admin-sessions-list">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onRevoke={handleRevokeSession}
                isCurrent={session.isCurrent}
              />
            ))}
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div className="admin-section-header">
            <div>
              <h3>Security Settings</h3>
              <p>Account security configuration</p>
            </div>
          </div>

          <div className="admin-security-settings">
            <div className="security-setting-item">
              <div className="security-setting-info">
                <div className="security-setting-icon">
                  <RiShieldCheckLine size={20} />
                </div>
                <div>
                  <div className="security-setting-label">Two-Factor Authentication</div>
                  <div className="security-setting-desc">Add an extra layer of security</div>
                </div>
              </div>
              <Tag color={adminDetails?.adminMetaData?.twoFactorEnabled ? "green" : "default"}>
                {adminDetails?.adminMetaData?.twoFactorEnabled ? "Enabled" : "Disabled"}
              </Tag>
            </div>

            <div className="security-setting-item">
              <div className="security-setting-info">
                <div className="security-setting-icon">
                  <FiMail size={20} />
                </div>
                <div>
                  <div className="security-setting-label">Email Verified</div>
                  <div className="security-setting-desc">Account email verification status</div>
                </div>
              </div>
              <Tag color={adminDetails?.adminMetaData?.emailVerified ? "green" : "orange"}>
                {adminDetails?.adminMetaData?.emailVerified ? "Verified" : "Pending"}
              </Tag>
            </div>

            <div className="security-setting-item">
              <div className="security-setting-info">
                <div className="security-setting-icon">
                  <FiLock size={20} />
                </div>
                <div>
                  <div className="security-setting-label">Password</div>
                  <div className="security-setting-desc">Last changed: {adminDetails?.adminMetaData?.passwordChangedAt ? new Date(adminDetails.adminMetaData.passwordChangedAt).toLocaleDateString() : "Never"}</div>
                </div>
              </div>
              <Button
                type="text"
                onClick={() => {
                  Modal.confirm({
                    title: "Force Password Reset",
                    content: "This will send a password reset email to the user.",
                    okText: "Send Reset Email",
                    onOk: () => {
                      notification.success({ message: "Password reset email sent" });
                    },
                  });
                }}
              >
                Force Reset
              </Button>
            </div>

            <div className="security-setting-item">
              <div className="security-setting-info">
                <div className="security-setting-icon">
                  <FiCalendar size={20} />
                </div>
                <div>
                  <div className="security-setting-label">Account Created</div>
                  <div className="security-setting-desc">
                    {adminDetails?.adminMetaData?.createdAt
                      ? new Date(adminDetails.adminMetaData.createdAt).toLocaleDateString()
                      : "Unknown"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  // Render Notes Tab
  const renderNotesTab = () => (
    <div className="admin-notes-tab">
      <div className="admin-section-header">
        <div>
          <h3>Internal Notes</h3>
          <p>Private notes visible only to super admins</p>
        </div>
        <Button
          className="admin-details-btn-primary"
          icon={<FiPlus style={{ marginRight: 6 }} />}
          onClick={() => setAddNoteModalVisible(true)}
        >
          Add Note
        </Button>
      </div>

      {notes.length > 0 ? (
        <div className="admin-notes-list">
          {notes.map((note) => (
            <NoteCard
              key={note._id}
              note={note}
              onEdit={(n) => {
                setNewNote(n.content);
                setNoteTags(n.tags || []);
                setAddNoteModalVisible(true);
              }}
              onDelete={(noteId) => {
                setNotes(notes.filter((n) => n._id !== noteId));
                notification.success({ message: "Note deleted" });
              }}
            />
          ))}
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notes yet"
        >
          <Button type="primary" onClick={() => setAddNoteModalVisible(true)}>
            Add First Note
          </Button>
        </Empty>
      )}
    </div>
  );

  // Render history cards
  const renderHistoryCards = () => (
    <div>
      <h3 style={{ marginBottom: 8, color: "#1e293b", fontWeight: 600 }}>
        Account History
      </h3>
      <p style={{ color: "#64748b", marginBottom: 20 }}>
        View detailed historical data for this admin account
      </p>

      <Row gutter={[20, 20]} className="admin-history-cards-row">
        <Col xs={24} sm={12} lg={8}>
          <Card
            className="admin-history-card"
            bordered={false}
            onClick={() => history.push(`/super-admin/admin/${id}/usage-history`)}
          >
            <div className="admin-history-card-icon purple">
              <FiFileText size={24} />
            </div>
            <div className="admin-history-card-title">Usage History</div>
            <div className="admin-history-card-desc">
              View detailed record of credit usage, allocations, and utilization over time.
            </div>
            <div className="admin-history-card-arrow">
              View details <FiArrowRight size={16} />
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card
            className="admin-history-card"
            bordered={false}
            onClick={() => history.push(`/super-admin/admin/${id}/subscription-history`)}
          >
            <div className="admin-history-card-icon green">
              <FiDollarSign size={24} />
            </div>
            <div className="admin-history-card-title">Subscription History</div>
            <div className="admin-history-card-desc">
              Review past and current subscription plans, changes, and renewal information.
            </div>
            <div className="admin-history-card-arrow">
              View details <FiArrowRight size={16} />
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card
            className="admin-history-card"
            bordered={false}
            onClick={() => history.push(`/super-admin/admin/${id}/payment-history`)}
          >
            <div className="admin-history-card-icon blue">
              <FiCreditCard size={24} />
            </div>
            <div className="admin-history-card-title">Payment History</div>
            <div className="admin-history-card-desc">
              Access complete payment records including invoices and transaction details.
            </div>
            <div className="admin-history-card-arrow">
              View details <FiArrowRight size={16} />
            </div>
          </Card>
        </Col>
      </Row>

      <Alert
        className="admin-info-alert"
        type="info"
        showIcon
        message="History Analytics"
        description="Use these historical views to analyze trends, identify issues, and understand usage patterns for this admin account."
        style={{ marginTop: 24 }}
      />
    </div>
  );

  return (
    <div className="admin-details-container">
      {/* Header */}
      <div className="admin-details-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <h1 style={{ margin: 0 }}>Admin Details</h1>
            {adminDetails?.adminMetaData?.disabled && (
              <Tag color="red" icon={<FiLock size={12} />}>Disabled</Tag>
            )}
            {adminDetails?.currentSubscription?.status === "active" && (
              <Tag color="green" icon={<FiCheckCircle size={12} />}>Active Subscription</Tag>
            )}
          </div>
          <p style={{ margin: 0 }}>
            {adminDetails?.adminMetaData?.email || "Loading..."}
            {adminDetails?.adminMetaData?.name && ` - ${adminDetails.adminMetaData.name}`}
          </p>
        </div>
        <div className="admin-details-actions">
          <Button
            className="admin-details-btn-refill"
            onClick={handleManualRefill}
            loading={refillLoading}
            icon={<FiRefreshCw style={{ marginRight: 6 }} />}
          >
            Refill Credits
          </Button>
          <Button
            className="admin-details-btn-primary"
            onClick={() => setChangePlanModalVisible(true)}
            icon={<RiVipCrownLine style={{ marginRight: 6 }} />}
          >
            Change Plan
          </Button>
          <Dropdown overlay={quickActionsMenu} trigger={["click"]} placement="bottomRight">
            <Button className="admin-details-btn-secondary" icon={<FiMoreVertical />}>
              More Actions
            </Button>
          </Dropdown>
          <Button
            className="admin-details-btn-secondary"
            onClick={() => history.goBack()}
            icon={<FiArrowLeft style={{ marginRight: 6 }} />}
          >
            Go Back
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="admin-details-loading">
          <Spin size="large" />
          <span className="admin-details-loading-text">Loading admin details...</span>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          {renderStatsCards()}

          {/* Tabs Section */}
          <div className="admin-details-tabs-section">
            <Tabs defaultActiveKey="1" onChange={handleTabChange}>
              <TabPane
                tab={
                  <span>
                    <FiUser className="admin-tab-icon" />
                    Account
                  </span>
                }
                key="1"
              >
                <MetaData
                  adminMetaData={adminDetails?.adminMetaData}
                  handleToggleUserDisable={handleToggleUserDisable}
                  workspaceData={adminDetails?.workspaceData}
                />
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiSettings className="admin-tab-icon" />
                    Analytics
                  </span>
                }
                key="2"
              >
                <Analytics analytics={adminDetails?.analytics} />
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiFileText className="admin-tab-icon" />
                    Usage
                  </span>
                }
                key="3"
              >
                <UsageDetails
                  currentUsageDetails={adminDetails?.currentUsageDetails}
                />
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiDollarSign className="admin-tab-icon" />
                    Subscription
                  </span>
                }
                key="4"
              >
                <CurrentSubscription
                  currentSubscription={adminDetails?.currentSubscription}
                />
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiUsers className="admin-tab-icon" />
                    Team
                    {teamMembers.length > 0 && (
                      <Badge count={teamMembers.length} style={{ marginLeft: 8, backgroundColor: "#8B5CF6" }} />
                    )}
                  </span>
                }
                key="5"
              >
                {renderTeamTab()}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiActivity className="admin-tab-icon" />
                    Activity
                  </span>
                }
                key="6"
              >
                {renderActivityTab()}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiShield className="admin-tab-icon" />
                    Security
                  </span>
                }
                key="7"
              >
                {renderSecurityTab()}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiMessageSquare className="admin-tab-icon" />
                    Notes
                    {notes.length > 0 && (
                      <Badge count={notes.length} style={{ marginLeft: 8, backgroundColor: "#f59e0b" }} />
                    )}
                  </span>
                }
                key="8"
              >
                {renderNotesTab()}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <FiClock className="admin-tab-icon" />
                    History
                  </span>
                }
                key="9"
              >
                {renderHistoryCards()}
              </TabPane>
            </Tabs>
          </div>
        </>
      )}

      {/* Change Plan Modal */}
      <Modal
        className="admin-modal"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <RiVipCrownLine size={20} color="#8B5CF6" />
            Change Subscription Plan
          </div>
        }
        visible={changePlanModalVisible}
        onCancel={() => {
          setChangePlanModalVisible(false);
          setSelectedPlan(null);
        }}
        footer={[
          <Button
            key="cancel"
            className="admin-details-btn-secondary"
            onClick={() => {
              setChangePlanModalVisible(false);
              setSelectedPlan(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-details-btn-primary"
            disabled={!selectedPlan}
            onClick={handleChangePlan}
          >
            Change Plan
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12, color: "#64748b" }}>
            Current plan: <strong>{currentPlan}</strong>
          </div>
          <Select
            style={{ width: "100%" }}
            placeholder="Select new plan"
            value={selectedPlan}
            onChange={setSelectedPlan}
            size="large"
          >
            {availablePlans.map((plan) => (
              <Option key={plan.id} value={plan.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: plan.color,
                    }}
                  />
                  {plan.name}
                </div>
              </Option>
            ))}
          </Select>
        </div>
        <Alert
          message="Plan changes take effect immediately"
          description="This will update credits and usage limits. The user will be notified via email."
          type="info"
          showIcon
        />
      </Modal>

      {/* Add Note Modal */}
      <Modal
        className="admin-modal"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FiMessageSquare size={20} color="#8B5CF6" />
            Add Internal Note
          </div>
        }
        visible={addNoteModalVisible}
        onCancel={() => {
          setAddNoteModalVisible(false);
          setNewNote("");
          setNoteTags([]);
        }}
        footer={[
          <Button
            key="cancel"
            className="admin-details-btn-secondary"
            onClick={() => {
              setAddNoteModalVisible(false);
              setNewNote("");
              setNoteTags([]);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-details-btn-primary"
            disabled={!newNote.trim()}
            onClick={handleAddNote}
          >
            Add Note
          </Button>,
        ]}
      >
        <TextArea
          rows={4}
          placeholder="Enter your note here..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder="Add tags (optional)"
          value={noteTags}
          onChange={setNoteTags}
        >
          <Option value="follow-up">follow-up</Option>
          <Option value="urgent">urgent</Option>
          <Option value="support">support</Option>
          <Option value="enterprise">enterprise</Option>
          <Option value="churn-risk">churn-risk</Option>
        </Select>
      </Modal>

      {/* Send Email Modal */}
      <Modal
        className="admin-modal"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FiMail size={20} color="#8B5CF6" />
            Send Email
          </div>
        }
        visible={sendEmailModalVisible}
        onCancel={() => setSendEmailModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            className="admin-details-btn-secondary"
            onClick={() => setSendEmailModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="admin-details-btn-primary"
            icon={<FiSend style={{ marginRight: 6 }} />}
          >
            Send Email
          </Button>,
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>To</label>
          <Input value={adminDetails?.adminMetaData?.email || ""} disabled />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Subject</label>
          <Input placeholder="Enter email subject" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Message</label>
          <TextArea rows={6} placeholder="Enter your message..." />
        </div>
        <Alert
          message="Email will be sent from your configured SMTP server"
          type="info"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default AdminDetails;
